import { components as c } from "@/components";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@patient-watch/ui/button";
import { Form } from "@patient-watch/ui/form";
import ContactPersonComboBox from "@/features/shared_attributes/components/ContactPersonComboBox";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";

const contactFormSchema = z.object({
    contact_id: z.string().uuid(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const AddContactPage = () => {
    const copy = usePatientsCopy();
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { organisationId, defaultContactId } = Route.useLoaderData();
    const navigate = useNavigate();

    const form = useForm<ContactFormValues>({
        resolver: zodResolver(contactFormSchema),
        defaultValues: {
            contact_id: defaultContactId ?? search.contact_id ?? undefined,
        },
    });

    const handleSubmit = (data: ContactFormValues) => {
        void navigate({
            to: "/patients/$diaryId/update/$eventId/$trackerId/message",
            params: { diaryId, eventId, trackerId },
            search: { ...search, contact_id: data.contact_id },
        });
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
                <c.cc.Title words={copy.contact} description={copy.contactStepDescription} />
                <ContactPersonComboBox organisationId={organisationId} />
                <Button size="lg" className="w-full" type="submit">
                    {copy.continueLabel}
                </Button>
            </form>
        </Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/contact")({
    component: AddContactPage,
    validateSearch: updateDiaryFlowSearchSchema,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        return {
            organisationId: diary?.organisation_id,
            defaultContactId: diary?.shared_attributes?.contact_id ?? null,
        };
    },
});
